import { IComponentStyles } from '@fluentui/foundation-legacy';
import { Icon } from '@fluentui/react';
import { DefaultButton } from '@fluentui/react/lib/Button';
import { IPanelProps, Panel } from '@fluentui/react/lib/Panel';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { AutoTaggingJobStore } from 'src/customText/core/stores/CustomTextAutoTaggingJobStore';
import { GptJobStore } from 'src/modules/gptPrediction/stores/GptJobStore';
import { jobStatusPrettifier } from 'src/modules/modelTraining/utils/modelJobStatusPrettifier';

const messages = defineMessages({
    hours: { id: 'customText.autoTaggingJobDetailsPanel.hours', defaultMessage: 'hours' },
    close: { id: 'customText.autoTaggingJobDetailsPanel.close', defaultMessage: 'Close' },
    minutes: { id: 'customText.autoTaggingJobDetailsPanel.minutes', defaultMessage: 'minutes' },
    seconds: { id: 'customText.autoTaggingJobDetailsPanel.seconds', defaultMessage: 'seconds' },
    jobStatus: { id: 'customText.autoTaggingJobDetailsPanel.jobStatus', defaultMessage: 'Status' },
    panelClose: { id: 'customText.autoTaggingJobDetailsPanel.panelClose', defaultMessage: 'Close' },
    jobId: { id: 'customText.autoTaggingJobDetailsPanel.jobId', defaultMessage: 'Auto-label job ID' },
    totalTime: { id: 'customText.autoTaggingJobDetailsPanel.totalTime', defaultMessage: 'Total time' },
    triggeredOn: { id: 'customText.autoTaggingJobDetailsPanel.triggeredOn', defaultMessage: 'Started at' },
    trainedModel: { id: 'customText.autoTaggingJobDetailsPanel.trainedModel', defaultMessage: 'Trained with' },
    jobHasFailed: { id: 'customText.autoTaggingJobDetailsPanel.jobHasFailed', defaultMessage: 'Job has failed' },
    filesSelected: { id: 'customText.autoTaggingJobDetailsPanel.filesSelected', defaultMessage: 'Files selected' },
    consumedQuota: { id: 'customText.autoTaggingJobDetailsPanel.consumedQuota', defaultMessage: 'Consumed quota' },
    errorDetails: { id: 'customText.autoTaggingJobDetailsPanel.errorDetails', defaultMessage: 'Show error details' },
    panelTitle: { id: 'customText.autoTaggingJobDetailsPanel.panelTitle', defaultMessage: 'Auto-label job details' },
    jobInProgress: { id: 'customText.autoTaggingJobDetailsPanel.jobInProgress', defaultMessage: 'Job still in progress' },
    entitiesSelected: { id: 'customText.autoTaggingJobDetailsPanel.entitiesSelected', defaultMessage: 'Entities selected' }
});

export type AutoTaggingJobDetailsPanelProps = IPanelProps & {
    isOpen: boolean;
    isGptJob: boolean;
    onDismiss: () => void;
    onModelClick: (modelId: string) => void;
    autoTaggingJob?: AutoTaggingJobStore | GptJobStore;
};

const panelChildrenContentStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { marginTop: '40px' }
});

export const AutoTaggingJobDetailsPanel = observer((props: AutoTaggingJobDetailsPanelProps) => {
    const { isOpen = false, isGptJob, onDismiss, autoTaggingJob = new AutoTaggingJobStore(''), onModelClick } = props;

    const { localize } = useLocalization();

    const [totalElapsedTime, setTotalElapsedTime] = React.useState('');
    const [areErrorDetailsVisible, setAreErrorDetailsVisible] = React.useState(false);

    const getStatusTimeFormat = (elapsedTime: string) =>
        elapsedTime
            ? `${Number(elapsedTime.split(':')[0])} ${localize(messages.hours)}, ${Number(elapsedTime.split(':')[1])} ${localize(
                  messages.minutes
              )}, ${Number(elapsedTime.split(':')[2])} ${localize(messages.seconds)}`
            : '--';

    React.useEffect(() => {
        switch (autoTaggingJob.status) {
            case 'inProgress':
                setTotalElapsedTime(localize(messages.jobInProgress));
                return;
            case 'failed':
                setTotalElapsedTime(localize(messages.jobHasFailed));
                return;
            default:
                setTotalElapsedTime(getStatusTimeFormat(autoTaggingJob.elapsedTime));
        }
    }, [autoTaggingJob.status, autoTaggingJob]);

    const getPanelStackElement = (
        title: ReactIntl.FormattedMessage.MessageDescriptor,
        details: string | number,
        extraDetails?: string | number
    ) => (
        <Stack tokens={{ childrenGap: 10 }}>
            <LuisText fontWeight="bold">{localize(title)}</LuisText>
            {extraDetails ? (
                <Stack tokens={{ childrenGap: 5 }}>
                    <LuisText>{details}</LuisText>
                    <LuisText>{extraDetails}</LuisText>
                </Stack>
            ) : (
                <LuisText>{details}</LuisText>
            )}
        </Stack>
    );

    const onRenderFooterContent = React.useCallback(
        () => (
            <div>
                <DefaultButton onClick={onDismiss} text={localize(messages.close)} />
            </div>
        ),
        [onDismiss]
    );

    return (
        <Panel
            isLightDismiss
            isOpen={isOpen}
            onDismiss={onDismiss}
            headerText={localize(messages.panelTitle)}
            closeButtonAriaLabel={localize(messages.panelClose)}
            onRenderFooterContent={onRenderFooterContent}
            isFooterAtBottom>
            <Stack styles={panelChildrenContentStyles} tokens={{ childrenGap: 20 }}>
                {getPanelStackElement(messages.jobId, autoTaggingJob.id)}

                {getPanelStackElement(
                    messages.triggeredOn,
                    autoTaggingJob.createdDateTime ? autoTaggingJob.createdDateTime.toLocaleString() : '--'
                )}

                {getPanelStackElement(messages.totalTime, totalElapsedTime)}

                {getPanelStackElement(messages.jobStatus, jobStatusPrettifier(autoTaggingJob.status))}

                {autoTaggingJob.status === 'failed' && !isGptJob && (
                    <Stack>
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                            <ExternalLink onClick={() => setAreErrorDetailsVisible(!areErrorDetailsVisible)}>
                                {localize(messages.errorDetails)}
                            </ExternalLink>
                            <Icon iconName={areErrorDetailsVisible ? 'ChevronUp' : 'ChevronDown'} />
                        </Stack>
                        {areErrorDetailsVisible && autoTaggingJob.errors.map(e => <li key={e.code}>{e.message}</li>)}
                    </Stack>
                )}

                <Stack tokens={{ childrenGap: 10 }}>
                    <LuisText fontWeight="bold">{localize(messages.trainedModel)}</LuisText>
                    {isGptJob ? (
                        <LuisText>Azure OpenAI’s GPT</LuisText>
                    ) : (
                        <ExternalLink onClick={() => onModelClick((autoTaggingJob as AutoTaggingJobStore).trainedModelLabel)}>
                            {(autoTaggingJob as AutoTaggingJobStore).trainedModelLabel ?? '--'}
                        </ExternalLink>
                    )}
                </Stack>

                {!isGptJob && [
                    <Stack tokens={{ childrenGap: 10 }}>
                        <LuisText fontWeight="bold">{localize(messages.entitiesSelected)}</LuisText>
                        <Stack>
                            {(autoTaggingJob as AutoTaggingJobStore).extractors.map((exName, i) => (i === 0 ? exName : `, ${exName}`))}
                        </Stack>
                    </Stack>,
                    <Stack tokens={{ childrenGap: 10 }}>
                        <LuisText fontWeight="bold">{localize(messages.filesSelected)}</LuisText>
                        <Stack>
                            {(autoTaggingJob as AutoTaggingJobStore).documents.map((docName, i) => (i === 0 ? docName : `, ${docName}`))}
                        </Stack>
                    </Stack>,
                    getPanelStackElement(
                        messages.consumedQuota,
                        autoTaggingJob.status === 'succeeded' ? (autoTaggingJob as AutoTaggingJobStore).totalCharsCount : '--'
                    )
                ]}
            </Stack>
        </Panel>
    );
});
